import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { createWebSocketConnection, disconnectWebSocket } from '@/services/websocket';
import { WS_EVENTS } from '@/lib/constants';
import {
  AgentUpdateEvent,
  AgentActionEvent,
  ConsoleUpdateEvent,
  AgentErrorEvent,
  UserMessageEvent,
  AgentStatus,
} from '@/types';
import { useAppConfig } from './AppConfigContext';

interface WebSocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  agentStatus: AgentStatus;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (message: UserMessageEvent) => void;
  clearConsole: () => void;

  // Event listeners
  onAgentUpdate: (callback: (event: AgentUpdateEvent) => void) => () => void;
  onAgentAction: (callback: (event: AgentActionEvent) => void) => () => void;
  onConsoleUpdate: (callback: (event: ConsoleUpdateEvent) => void) => () => void;
  onAgentError: (callback: (event: AgentErrorEvent) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { config } = useAppConfig();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('offline');

  // Store callbacks using refs to avoid re-renders
  const agentUpdateCallbacks = useRef<Set<(event: AgentUpdateEvent) => void>>(new Set());
  const agentActionCallbacks = useRef<Set<(event: AgentActionEvent) => void>>(new Set());
  const consoleUpdateCallbacks = useRef<Set<(event: ConsoleUpdateEvent) => void>>(new Set());
  const agentErrorCallbacks = useRef<Set<(event: AgentErrorEvent) => void>>(new Set());

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (socket?.connected) {
      console.warn('WebSocket already connected');
      return;
    }

    if (!config.websocketUrl || !config.authId) {
      console.error('Missing WebSocket configuration');
      return;
    }

    const newSocket = createWebSocketConnection({
      url: config.websocketUrl,
      path: config.websocketPath,
      authId: config.authId,
      agentName: config.agentName,
      onConnect: () => {
        setIsConnected(true);
      },
      onDisconnect: (reason) => {
        setIsConnected(false);
        setAgentStatus('offline');
        console.log('Disconnected:', reason);
      },
      onError: (error) => {
        console.error('WebSocket error:', error);
        setAgentStatus('offline');
      },
      onReconnect: (attemptNumber) => {
        console.log('Reconnected after', attemptNumber, 'attempts');
        setIsConnected(true);
      },
    });

    // Setup server event listeners
    setupEventListeners(newSocket);

    // Connect
    newSocket.connect();
    setSocket(newSocket);
  }, [config, socket]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (socket) {
      disconnectWebSocket(socket, config.agentName);
      setSocket(null);
      setIsConnected(false);
      setAgentStatus('offline');
    }
  }, [socket, config.agentName]);

  // Setup event listeners for server events
  const setupEventListeners = useCallback((sock: Socket) => {
    // Agent update event
    sock.on(WS_EVENTS.AGENT_UPDATE, (event: AgentUpdateEvent) => {
      console.log('📨 agent.update:', event);
      setAgentStatus(event.status);
      agentUpdateCallbacks.current.forEach(cb => cb(event));
    });

    // Agent action event
    sock.on(WS_EVENTS.AGENT_ACTION, (event: AgentActionEvent) => {
      console.log('📨 agent.action:', event);
      agentActionCallbacks.current.forEach(cb => cb(event));
    });

    // Console update event
    sock.on(WS_EVENTS.CONSOLE_UPDATE, (event: ConsoleUpdateEvent) => {
      console.log('📨 console.update:', event);
      consoleUpdateCallbacks.current.forEach(cb => cb(event));
    });

    // Agent error event
    sock.on(WS_EVENTS.AGENT_ERROR, (event: AgentErrorEvent) => {
      console.error('📨 agent.error:', event);
      agentErrorCallbacks.current.forEach(cb => cb(event));
    });
  }, []);

  // Send user message
  const sendMessage = useCallback((message: UserMessageEvent) => {
    if (!socket?.connected) {
      console.error('Cannot send message: WebSocket not connected');
      return;
    }

    console.log('📤 user.message:', message);
    socket.emit(WS_EVENTS.USER_MESSAGE, message);
  }, [socket]);

  // Clear console
  const clearConsole = useCallback(() => {
    if (!socket?.connected) {
      console.error('Cannot clear console: WebSocket not connected');
      return;
    }

    console.log('📤 console.clear');
    socket.emit(WS_EVENTS.CONSOLE_CLEAR, {
      timestamp: Date.now(),
    });
  }, [socket]);

  // Event listener registration functions
  const onAgentUpdate = useCallback((callback: (event: AgentUpdateEvent) => void) => {
    agentUpdateCallbacks.current.add(callback);
    return () => {
      agentUpdateCallbacks.current.delete(callback);
    };
  }, []);

  const onAgentAction = useCallback((callback: (event: AgentActionEvent) => void) => {
    agentActionCallbacks.current.add(callback);
    return () => {
      agentActionCallbacks.current.delete(callback);
    };
  }, []);

  const onConsoleUpdate = useCallback((callback: (event: ConsoleUpdateEvent) => void) => {
    consoleUpdateCallbacks.current.add(callback);
    return () => {
      consoleUpdateCallbacks.current.delete(callback);
    };
  }, []);

  const onAgentError = useCallback((callback: (event: AgentErrorEvent) => void) => {
    agentErrorCallbacks.current.add(callback);
    return () => {
      agentErrorCallbacks.current.delete(callback);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socket) {
        disconnectWebSocket(socket, config.agentName);
      }
    };
  }, [socket, config.agentName]);

  const value: WebSocketContextValue = {
    socket,
    isConnected,
    agentStatus,
    connect,
    disconnect,
    sendMessage,
    clearConsole,
    onAgentUpdate,
    onAgentAction,
    onConsoleUpdate,
    onAgentError,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
}
